// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import type { ProgressSummaryResponse, ProgressWeightListResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { Field } from "@/components/onboarding/Field"
import { WeightChart } from "./WeightChart"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import { WeightSparkline } from "./WeightSparkline"

/** Accepts "," or "." as the decimal separator — DE keyboards/locale habit. */
function parseWeightInput(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".")
  if (normalized === "") return null
  const n = Number(normalized)
  return Number.isFinite(n) && n > 0 ? n : null
}

function todayIsoDate(): string {
  const d = new Date()
  const tzOffsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10)
}

/**
 * ADR-027 rules 2-3 — weigh-ins, 7-day trend, plan consistency, calibration
 * state. Every number rendered here is an OBSERVABLE fact from
 * GET /v1/progress/summary; this component computes nothing itself and
 * never shows a predicted-loss/actual comparison or a success/failure
 * verdict, client or otherwise.
 */
export function ProgressSection() {
  const api = useApiClient()
  const { t } = useI18n()
  const [summary, setSummary] = useState<ProgressSummaryResponse | null>(null)
  const [weightHistory, setWeightHistory] = useState<ProgressWeightListResponse | null>(null)
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading")
  const [weightInput, setWeightInput] = useState("")
  const [dateInput, setDateInput] = useState(todayIsoDate)
  const [logState, setLogState] = useState<"idle" | "saving" | "saved" | "error">("idle")

  async function loadSummary() {
    try {
      const res = await api.getProgressSummary()
      api
        .getProgressWeightHistory()
        .then(setWeightHistory)
        .catch(err => {
          // Chart is enhancement — summary still renders. But NEVER fully
          // silent (2026-08-30: a server 500 here hid the chart for days
          // with zero signal anywhere).
          console.error("weight history fetch failed", err)
        })
      setSummary(res)
      setLoadState("ready")
    } catch {
      setLoadState("error")
    }
  }

  useEffect(() => {
    void loadSummary()
  }, [api])

  async function handleLogWeight() {
    const weightKg = parseWeightInput(weightInput)
    if (weightKg === null) {
      setLogState("error")
      return
    }
    setLogState("saving")
    try {
      await api.logProgressWeight({ weightKg, measuredAt: dateInput })
      setLogState("saved")
      api
        .getProgressWeightHistory()
        .then(setWeightHistory)
        .catch(() => {})
      setWeightInput("")
      await loadSummary()
    } catch {
      setLogState("error")
    }
  }

  if (loadState === "loading") return null
  if (loadState === "error" || !summary) {
    // Truthful degraded state (AGENTS.md) — never silently hide the surface.
    return (
      <div
        role="status"
        className="rounded-bb-xl border border-line bg-paper p-5 text-sm text-ink-2"
      >
        {t("insights.progress.unavailableBody")}
      </div>
    )
  }

  const { calibrationState, consistency, weightTrend } = summary

  return (
    <div className="flex flex-col gap-4 rounded-bb-xl border border-line bg-paper p-5">
      <h2 className="text-lg font-bold text-ink">{t("insights.progress.sectionTitle")}</h2>

      {/* Calibration state messaging — ADR-027 rule 3: the ONLY judgment-
          adjacent copy on this page, and even it never asserts a verdict. */}
      <div className="rounded-bb-md bg-cream p-3">
        <p className="text-sm font-semibold text-ink">
          {t(
            calibrationState === "calibrating"
              ? "insights.progress.calibratingTitle"
              : calibrationState === "observing"
                ? "insights.progress.observingTitle"
                : "insights.progress.reviewableTitle"
          )}
        </p>
        <p className="text-sm text-ink-2">
          {t(
            calibrationState === "calibrating"
              ? "insights.progress.calibratingBody"
              : calibrationState === "observing"
                ? "insights.progress.observingBody"
                : "insights.progress.reviewableBody"
          )}
        </p>
        {calibrationState === "reviewable" && (
          <Button
            type="button"
            variant="outline"
            className="mt-2 min-h-11"
            onClick={() => openAmyWithPrompt(t("insights.progress.reviewPrompt"))}
          >
            {t("insights.progress.reviewCta")}
          </Button>
        )}
      </div>

      {/* Weight quick-log */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-ink">
          {t("insights.progress.weightLogTitle")}
        </span>
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t("insights.progress.weightInputLabel")} htmlFor="progress-weight-kg">
            <Input
              id="progress-weight-kg"
              type="text"
              inputMode="decimal"
              value={weightInput}
              onChange={e => {
                setWeightInput(e.target.value)
                setLogState("idle")
              }}
              className="w-28"
            />
          </Field>
          <Field
            label={t("insights.progress.weightDateLabel")}
            htmlFor="progress-weight-date"
            required={false}
          >
            <Input
              id="progress-weight-date"
              type="date"
              value={dateInput}
              max={todayIsoDate()}
              onChange={e => setDateInput(e.target.value)}
              className="w-40"
            />
          </Field>
          <Button
            type="button"
            className="min-h-11"
            onClick={() => void handleLogWeight()}
            disabled={logState === "saving"}
          >
            {t("insights.progress.logButton")}
          </Button>
        </div>
        {logState === "saved" && (
          <p className="text-xs text-green">{t("insights.progress.loggedConfirmation")}</p>
        )}
        {logState === "error" && (
          <p className="text-xs text-destructive">{t("insights.progress.weightErrorInvalid")}</p>
        )}
      </div>

      {weightHistory && weightHistory.entries.length > 0 && summary && (
        <WeightChart entries={weightHistory.entries} trend={summary.weightTrend} />
      )}

      {/* 7-day trend */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold text-ink">{t("insights.progress.trendTitle")}</span>
        {weightTrend && weightTrend.series.length >= 2 ? (
          <>
            <WeightSparkline
              points={weightTrend.series}
              ariaLabel={t("insights.progress.trendTitle")}
            />
            <p className="text-xs text-ink-2">
              {t(
                weightTrend.trendDirection === "down"
                  ? "insights.progress.trendDirectionDown"
                  : weightTrend.trendDirection === "up"
                    ? "insights.progress.trendDirectionUp"
                    : weightTrend.trendDirection === "flat"
                      ? "insights.progress.trendDirectionFlat"
                      : "insights.progress.trendDirectionPending"
              )}
            </p>
          </>
        ) : (
          <p className="text-sm text-ink-2">{t("insights.progress.trendEmpty")}</p>
        )}
        {/* Contextual guide link: the place users stare at a barely-moving
            number is exactly where the why-the-scale-lies guide belongs. */}
        <Link
          to="/guides/why-the-scale-lies"
          className="mt-1 self-start text-xs font-semibold text-green hover:underline"
        >
          {t("insights.progress.scaleGuideLink")} →
        </Link>
      </div>

      {/* Plan consistency — observable framing only */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-ink">
          {t("insights.progress.consistencyTitle")}
        </span>
        {consistency ? (
          <p className="text-sm text-ink-2">
            {t("insights.progress.consistencyBody", {
              percent: String(consistency.consistencyPercent),
            })}
          </p>
        ) : (
          <p className="text-sm text-ink-2">{t("insights.progress.consistencyEmpty")}</p>
        )}
      </div>
    </div>
  )
}
